import { useEffect, useRef, useState } from 'react';

import { CircleAlertIcon, FolderIcon } from 'lucide-react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import GoogleDrive from '@/components/atoms/Icons/GoogleDrive';
import {
  SidebarWrapperContent,
  SidebarWrapperFooter,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import useDatasources from '@/hooks/useDatasources';
import { openPublishModal } from '@/redux/collections/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { DatasourceStatus, DatasourceType, GoogleDriveConfig } from '@/shared/types/collections';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { noop, useHasDirtyFields } from '@/util/util';
import { LENGTH_XS } from '@/util/validation/constants';

import {
  buildGoogleDriveDatasource,
  canReadFolder,
  canSaveGoogleDrive,
  exchangeAuthorizationCode,
  GoogleDriveFormValues,
  GoogleDriveTokens,
  isGoogleDriveConfig,
  loadPicker,
  pickFolder,
  requestAuthorizationCode,
  useGoogleDriveSettings,
  useSignInScript,
} from './googleDrive';
import { EXTERNAL_SOURCE_FIELD, EXTERNAL_SOURCE_FOOTER, EXTERNAL_SOURCE_SECTION } from './layout';
import { useExternalSourceDatasourceId } from './useExternalSourceDatasourceId';

type FormValues = GoogleDriveFormValues & { keep_published?: boolean };

type GoogleDriveFormProps = { handleCancel: (withValidation?: boolean) => void };

const DIRTY = { shouldDirty: true };

export const GoogleDriveForm = ({ handleCancel }: GoogleDriveFormProps) => {
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );
  const dispatch = useDispatch();
  const { collectionId } = useParams();
  const { t } = useTranslation();
  const form = useFormContext<FormValues>();
  const datasourceId = useExternalSourceDatasourceId();
  const { datasource, getStatus, createDatasourceAsync, updateDatasourceAsync } = useDatasources(
    collectionId,
    datasourceId
  );
  const { data: settings, isError: isSettingsError } = useGoogleDriveSettings();
  const isEnabled = !!settings?.enabled;
  const { data: isSignInLoaded } = useSignInScript(canWrite && isEnabled);
  const [isConnecting, setIsConnecting] = useState(false);
  const canConnect = canWrite && isEnabled && !!isSignInLoaded && !isConnecting;
  const initializedRef = useRef(false);

  const editing =
    !!datasourceId && getStatus === 'success' && datasource.type === DatasourceType.GDRIVE
      ? datasource
      : undefined;
  const isInvalid = editing?.status === DatasourceStatus.INVALID;
  const showKeepPublished = !!editing && !editing.is_draft && !isInvalid;
  const isNotConfigured = !!editing && (isSettingsError || settings?.enabled === false);

  const [name, refreshToken, folderId, folderName] = useWatch({
    control: form.control,
    name: ['name', 'refresh_token', 'folder_id', 'folder_name'],
  });
  const { isSubmitting } = useFormState({ control: form.control });
  const isDirty = useHasDirtyFields(form.control);
  const canSave =
    canWrite &&
    !isConnecting &&
    canSaveGoogleDrive({ name, refresh_token: refreshToken, folder_id: folderId }, !!editing) &&
    (!editing || isDirty);

  useEffect(() => {
    if (initializedRef.current || !editing || !isGoogleDriveConfig(editing.config)) {
      return;
    }
    initializedRef.current = true;
    form.resetField('name', { defaultValue: editing.name });
    form.resetField('folder_id', { defaultValue: editing.config.folder_id });
    form.resetField('folder_name', { defaultValue: editing.config.folder_name ?? '' });
    form.resetField('keep_published', { defaultValue: true });
  }, [editing, form]);

  const authorize = async (): Promise<GoogleDriveTokens | null> => {
    if (!settings?.enabled) {
      return null;
    }
    const code = await requestAuthorizationCode(settings.client_id);
    if (!code) {
      return null;
    }
    return exchangeAuthorizationCode(code);
  };

  const withConnecting = async (action: () => Promise<void>) => {
    setIsConnecting(true);
    try {
      await action();
    } catch {
      toast.error(t('collections.google_drive.connect_error'));
    } finally {
      setIsConnecting(false);
    }
  };

  const handleChooseFolder = () =>
    withConnecting(async () => {
      loadPicker().catch(noop);
      const tokens = await authorize();
      if (!tokens || !settings?.enabled) {
        return;
      }
      const folder = await pickFolder(settings, tokens.access_token);
      if (!folder) {
        return;
      }
      form.setValue('refresh_token', tokens.refresh_token, DIRTY);
      form.setValue('folder_id', folder.id, DIRTY);
      form.setValue('folder_name', folder.name, DIRTY);
      if (!form.getValues('name')) {
        form.setValue('name', folder.name.trim().slice(0, LENGTH_XS), {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    });

  const findReadableFolder = async (config: GoogleDriveConfig, accessToken: string) => {
    const isReadable = await canReadFolder(config.folder_id, accessToken);
    if (isReadable) {
      return { folder_id: config.folder_id, folder_name: config.folder_name };
    }
    if (!settings?.enabled) {
      return null;
    }
    const folder = await pickFolder(settings, accessToken);
    return folder && { folder_id: folder.id, folder_name: folder.name };
  };

  const handleReconnect = () =>
    withConnecting(async () => {
      const tokens = await authorize();
      if (!tokens || !editing || !isGoogleDriveConfig(editing.config)) {
        return;
      }
      const folder = await findReadableFolder(editing.config, tokens.access_token);
      if (!folder) {
        return;
      }
      try {
        await updateDatasourceAsync({
          datasource_id: editing.datasource_id,
          type: DatasourceType.GDRIVE,
          name: editing.name,
          config: { ...folder, refresh_token: tokens.refresh_token },
        });
      } catch {
        // The mutation shows its own error toast; the connect error would be wrong here.
        return;
      }
      form.resetField('folder_id', { defaultValue: folder.folder_id });
      form.resetField('folder_name', { defaultValue: folder.folder_name ?? '' });
    });

  const onSubmit = async (data: FormValues) => {
    const payload = buildGoogleDriveDatasource(data);

    if (editing) {
      await updateDatasourceAsync({
        datasource_id: editing.datasource_id,
        ...payload,
        ...(showKeepPublished && { is_draft: !data.keep_published }),
      });
      handleCancel();
      return;
    }

    await createDatasourceAsync(payload);
    toast(t('collections.toast_messages.external_source_uploaded'), {
      description: t('collections.toast_messages.external_source_draft'),
      action: { label: t('collections.publish'), onClick: () => dispatch(openPublishModal()) },
    });
    handleCancel();
  };

  return (
    <>
      <SidebarWrapperContent className={EXTERNAL_SOURCE_SECTION}>
        {isInvalid && (
          <Alert variant="destructive">
            <CircleAlertIcon />
            <AlertTitle>{t('collections.google_drive.invalid_title')}</AlertTitle>
            <AlertDescription className="flex flex-col items-start gap-3">
              {t('collections.google_drive.invalid_description')}
              <Button
                type="button"
                size="sm"
                variant="outline-neutral"
                onClick={handleReconnect}
                isLoading={isConnecting}
                disabled={!canConnect}
              >
                {t('collections.google_drive.reconnect')}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <FormField
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <FormItem className={EXTERNAL_SOURCE_FIELD}>
              <FormLabel>{t('common.name')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value ?? ''}
                  onBlur={() => {
                    field.onBlur();
                    form.trigger('name');
                  }}
                  onChange={(e) => {
                    field.onChange(e);
                    if (fieldState.isTouched) {
                      form.trigger('name');
                    }
                  }}
                  placeholder={t('common.name_placeholder')}
                  disabled={!canWrite}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className={EXTERNAL_SOURCE_FIELD}>
          <span className="text-sm leading-5 font-medium">
            {t('collections.google_drive.folder')}
          </span>
          {isNotConfigured && (
            <p className="text-xs leading-5 text-foreground-muted">
              {t('collections.google_drive.not_configured')}
            </p>
          )}
          {folderId ? (
            <div className="flex w-full items-center justify-between gap-2 rounded-md border border-stroke px-3 py-2">
              <span className="flex min-w-0 items-center gap-2 text-sm leading-5">
                <FolderIcon size={16} className="shrink-0 text-foreground-muted" />
                <span className="truncate">{folderName || folderId}</span>
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline-neutral"
                onClick={handleChooseFolder}
                isLoading={isConnecting}
                disabled={!canConnect}
              >
                {t('collections.google_drive.change_folder')}
              </Button>
            </div>
          ) : (
            <>
              <p className="text-xs leading-5 text-foreground-muted">
                {t('collections.google_drive.connect_description')}
              </p>
              <Button
                type="button"
                variant="outline-neutral"
                onClick={handleChooseFolder}
                isLoading={isConnecting}
                disabled={!canConnect}
              >
                <GoogleDrive size={16} />
                {t('collections.google_drive.connect')}
              </Button>
            </>
          )}
        </div>

        {showKeepPublished && (
          <FormField
            control={form.control}
            name="keep_published"
            render={({ field }) => (
              <FormItem className="flex gap-2 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={!canWrite}
                  />
                </FormControl>
                <FormLabel className="flex flex-col items-start gap-1 leading-none">
                  {t('collections.add_faq.keep_published')}
                  <FormDescription>
                    {t('collections.add_faq.keep_published_description')}
                  </FormDescription>
                </FormLabel>
              </FormItem>
            )}
          />
        )}
      </SidebarWrapperContent>

      <SidebarWrapperFooter className={EXTERNAL_SOURCE_FOOTER}>
        <Button type="button" variant="ghost-neutral" onClick={() => handleCancel(true)}>
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          onClick={form.handleSubmit(onSubmit)}
          disabled={!canSave}
          isLoading={isSubmitting}
        >
          {editing ? t('common.update') : t('common.add')}
        </Button>
      </SidebarWrapperFooter>
    </>
  );
};
